import {
  Building2,
  Button,
  Filter,
  Input,
  Plus,
  RefreshCw,
  Search,
  SortAsc,
  Upload,
  useDebounce,
} from "@ews-admin/global-design-system";
import { Select } from "../../components/atoms/Select";
import { facilityTypeLabel } from "../../utils/enum-label-utils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AddFacilityModal } from "../../components/molecules/AddFacilityModal/AddFacilityModal";
import { BulkFacilityImportModal } from "../../components/molecules/BulkFacilityImportModal/BulkFacilityImportModal";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { FacilityCard } from "../../components/molecules/FacilityCard/FacilityCard";
import { Pagination } from "../../components/molecules/Pagination";
import { facilitiesApi } from "../../services/facilities.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  searchFacilitiesRequest,
  selectFacilitiesError,
  selectFacilitiesLoading,
  selectSearchResults,
} from "../../store/reducers/facilitySlice";
import { Facility, FacilityType } from "../../types/facility.definition";

export const FacilityManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux state
  const searchResults = useAppSelector(selectSearchResults);
  const isLoading = useAppSelector(selectFacilitiesLoading);
  const error = useAppSelector(selectFacilitiesError);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<number | null>(null);
  // MED-1532: reactivation is confirmed before it happens, the way disabling
  // already is on the detail page.
  const [reactivateTarget, setReactivateTarget] = useState<Facility | null>(
    null
  );

  // Filter and sort state
  const [filters, setFilters] = useState({
    facilityType: "",
    status: "",
    isPartner: "",
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showFilters, setShowFilters] = useState(false);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle search with debounce, filters, and sorting
  useEffect(() => {
    // Always trigger search when filters, sort, or search term change
    setCurrentPage(1);
    dispatch(
      searchFacilitiesRequest({
        keyword: debouncedSearchTerm,
        offset: 0,
        limit: itemsPerPage,
        filters: {
          facilityType: filters.facilityType || undefined,
          status: filters.status || undefined,
          isPartner: filters.isPartner || undefined,
        },
        sortBy: sortBy,
        sortOrder: sortOrder,
      })
    );
  }, [debouncedSearchTerm, dispatch, itemsPerPage, filters, sortBy, sortOrder]);

  const handleRefresh = async () => {
    setCurrentPage(1);
    setIsRefreshing(true);
    dispatch(
      searchFacilitiesRequest({
        keyword: debouncedSearchTerm,
        offset: 0,
        limit: itemsPerPage,
        filters: {
          facilityType: filters.facilityType || undefined,
          status: filters.status || undefined,
          isPartner: filters.isPartner || undefined,
        },
        sortBy: sortBy,
        sortOrder: sortOrder,
      })
    );
    setIsRefreshing(false);
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    const offset = (page - 1) * itemsPerPage;

    dispatch(
      searchFacilitiesRequest({
        keyword: debouncedSearchTerm,
        offset: offset,
        limit: itemsPerPage,
        filters: {
          facilityType: filters.facilityType || undefined,
          status: filters.status || undefined,
          isPartner: filters.isPartner || undefined,
        },
        sortBy: sortBy,
        sortOrder: sortOrder,
      })
    );
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    // The useEffect will handle the API call
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value,
    }));
    setCurrentPage(1); // Reset to first page when filtering
    // The useEffect will handle the API call
  };

  const handleSortChange = (newSortBy: string) => {
    const newSortOrder =
      sortBy === newSortBy ? (sortOrder === "asc" ? "desc" : "asc") : "asc";
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sorting
    // The useEffect will handle the API call
  };

  const handleFacilityClick = (facility: Facility) => {
    navigate(`/admin/facilities/${facility.id}`);
  };

  // MED-1495: a disabled facility has no detail page, so its card is not a link
  // and this is the only way to bring it back. Nothing is flipped optimistically —
  // the list is re-read so the card only changes once the server has agreed.
  const handleReactivate = (facility: Facility) => {
    if (reactivatingId !== null) {
      return;
    }
    setReactivateTarget(facility);
  };

  const handleReactivateCancel = () => {
    if (reactivatingId !== null) {
      return;
    }
    setReactivateTarget(null);
  };

  const handleReactivateConfirm = async () => {
    const facility = reactivateTarget;
    if (!facility || reactivatingId !== null) {
      return;
    }
    setReactivatingId(facility.id);
    try {
      await facilitiesApi.restoreFacility(Number(facility.id));
      setReactivateTarget(null);
      toast.success(t("admin.facilities.disabledCard.reactivateSuccess"));
      await handlePageChange(currentPage);
    } catch {
      toast.error(t("admin.facilities.toggleStatus.enableError"));
    } finally {
      setReactivatingId(null);
    }
  };

  // Always use search results for consistent server-side filtering/sorting
  const displayFacilities = searchResults ? searchResults.facilities : [];
  const totalItems = searchResults ? searchResults.totalCount : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedFacilities = displayFacilities;

  return (
    <div className="page-container">
      {/* Header — MED-1481: at ~525px the three action buttons overflowed the
          row and "Ajouter un nouvel établissement" was cut off on the right.
          Stack the actions under the title on small screens and let them wrap. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.facilities.title")}
          </h2>
          <p className="text-neutral-600">{t("admin.facilities.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowBulkImportModal(true)}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            <span>{t("admin.facilities.bulkImport.button")}</span>
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.facilities.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 rounded-lg border bg-danger-50 border-danger-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-danger-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-danger-800">
                {t("common.error")}
              </h3>
              <div className="mt-2 text-sm text-danger-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    className="px-2 py-1.5 ml-2 text-sm font-medium text-danger-800 bg-danger-50 rounded-md hover:bg-danger-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-danger-50 focus:ring-danger-600"
                    onClick={() => dispatch(clearError())}
                  >
                    {t("common.dismiss")}
                  </button>
                  <button
                    type="button"
                    className="ml-3 bg-danger-50 px-2 py-1.5 rounded-md text-sm font-medium text-danger-800 hover:bg-danger-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-danger-50 focus:ring-danger-600"
                    onClick={handleRefresh}
                  >
                    {t("common.retry")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="page-section">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Input
              placeholder={t("admin.facilities.searchPlaceholder")}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 ${showFilters ? "bg-primary-50 border-primary-300" : ""}`}
            >
              <Filter className="w-4 h-4" />
              <span>{t("common.filter")}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSortChange("name")}
              className="flex items-center space-x-2"
            >
              <SortAsc className="w-4 h-4" />
              <span>{t("common.sort")}</span>
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="p-4 mt-4 rounded-lg border bg-neutral-50 border-neutral-200">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Facility Type Filter */}
              <div>
                <Select
                  label={t("admin.facilities.filters.facilityType")}
                  value={filters.facilityType}
                  onChange={(value: unknown) =>
                    handleFilterChange("facilityType", value as string)
                  }
                  options={[
                    { label: t("common.all"), value: "" },
                    // MED-1464: one option per FacilityType, labelled from the
                    // catalogue, rather than a hand-maintained list that had
                    // drifted out of step with the enum.
                    ...Object.values(FacilityType).map(type => ({
                      label: facilityTypeLabel(t, type),
                      value: type as string,
                    })),
                  ]}
                />
              </div>

              {/* Status Filter */}
              <div>
                <Select
                  label={t("admin.facilities.filters.status")}
                  value={filters.status}
                  onChange={(value: unknown) =>
                    handleFilterChange("status", value as string)
                  }
                  options={[
                    { label: t("common.all"), value: "" },
                    {
                      label: t("admin.facilities.status.active"),
                      value: "active",
                    },
                    {
                      label: t("admin.facilities.status.inactive"),
                      value: "inactive",
                    },
                  ]}
                />
              </div>

              {/* Partner Status Filter */}
              <div>
                <Select
                  label={t("admin.facilities.filters.partnerStatus")}
                  value={filters.isPartner}
                  onChange={(value: unknown) =>
                    handleFilterChange("isPartner", value as string)
                  }
                  options={[
                    { label: t("common.all"), value: "" },
                    {
                      label: t("admin.facilities.partner.partner"),
                      value: "partner",
                    },
                    {
                      label: t("admin.facilities.partner.nonPartner"),
                      value: "non-partner",
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="py-12 text-center">
          <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-neutral-900">
            {t("common.loading")}
          </h3>
          <p className="mt-1 text-sm text-neutral-500">
            {t("admin.facilities.loadingFacilities")}
          </p>
        </div>
      ) : (
        <>
          {/* MED-1452: this page lists the FULL directory — every facility, licensed or
              not — while the Aperçu and Analyses tiles count only the licensed ones.
              Saying so is what stops the three numbers reading as a contradiction. */}
          <p className="page-section text-sm text-neutral-500">
            {t("admin.facilities.showingResults", { count: totalItems })}
          </p>

          {/* Facilities Grid */}
          <div className="grid grid-cols-1 gap-6 page-section lg:grid-cols-2 xl:grid-cols-3">
            {paginatedFacilities.map(facility => (
              <FacilityCard
                key={facility.id}
                facility={facility}
                onClick={handleFacilityClick}
                onReactivate={handleReactivate}
                isReactivating={reactivatingId === facility.id}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="page-section">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showPageNumbers={true}
                maxVisiblePages={5}
                showItemsPerPageSelector={true}
                itemsPerPageOptions={[5, 10, 12, 25, 50]}
              />
            </div>
          )}

          {/* Empty State */}
          {paginatedFacilities.length === 0 && !isLoading && (
            <div className="py-12 text-center">
              <Building2 className="mx-auto w-12 h-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900">
                {searchTerm
                  ? t("admin.facilities.noSearchResults")
                  : t("admin.facilities.noFacilities")}
              </h3>
              <p className="mt-1 text-sm text-neutral-500">
                {searchTerm
                  ? t("admin.facilities.noSearchResultsDescription")
                  : t("admin.facilities.noFacilitiesDescription")}
              </p>
              <div className="mt-6">
                {searchTerm ? (
                  <Button variant="outline" onClick={() => setSearchTerm("")}>
                    {t("common.clearSearch")}
                  </Button>
                ) : (
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="mr-2 w-4 h-4" />
                    {t("admin.facilities.addNew")}
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <AddFacilityModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <ConfirmationModal
        isOpen={reactivateTarget !== null}
        onClose={handleReactivateCancel}
        onConfirm={handleReactivateConfirm}
        title={t("common.confirmations.enableFacility")}
        message={t("common.confirmations.enableFacilityMessage")}
        confirmLabel={t("admin.facilities.disabledCard.reactivate")}
        cancelLabel={t("common.cancel")}
        variant="success"
        isLoading={reactivatingId !== null}
      />

      <BulkFacilityImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
      />
    </div>
  );
};
